import React from 'react';
import NeonBorder from './common/NeonBorder';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiBookOpen, FiX } = FiIcons;

const UserManual = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <NeonBorder className="w-full max-w-4xl max-h-[90vh] bg-white shadow-2xl rounded-lg flex flex-col">
        <div className="p-6 bg-white rounded-lg flex flex-col h-full overflow-hidden">
          <div className="flex justify-between items-center mb-6 pb-4 border-b border-me-blue/10 shrink-0">
            <h2 className="text-2xl font-bold text-me-blue flex items-center gap-3">
              <SafeIcon icon={FiBookOpen} className="text-me-orange" />
              Benutzerhandbuch
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-me-blue">
              <SafeIcon icon={FiX} className="w-6 h-6" />
            </button>
          </div>

          <div className="overflow-y-auto pr-2 space-y-8 text-gray-700 leading-relaxed">
            <section>
              <h3 className="text-xl font-bold text-me-orange mb-3">Was macht dieses Tool?</h3>
              <p>
                Stell dir vor, du hast ein riesiges Projekt mit vielen Dateien auf deinem Computer. Wenn du eine KI (wie ChatGPT) fragen willst, was in diesen Dateien steht, müsstest du ihr jede einzelne Datei separat geben. Das dauert ewig und ist mühsam.
                <br/><br/>
                Dieses Tool macht es einfacher: Es nimmt alle deine Dateien, packt sie ordentlich zusammen und erstellt eine einzige, gut strukturierte Datei. Diese Datei kann die KI dann lesen und versteht sofort dein ganzes Projekt. So sparst du Zeit und Arbeit.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-me-orange mb-3">So lädst du Dateien hoch</h3>
              <p>Du hast zwei Möglichkeiten, deine Dateien in das Tool zu bringen:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li><strong>Klicke auf den Button:</strong> Oben siehst du zwei große Felder. Klicke einfach auf "Dateien hochladen" oder "Ordner hochladen". Es öffnet sich ein Fenster, in dem du deine Dateien auswählen kannst.</li>
                <li><strong>Ziehen und Ablegen (Drag-and-Drop):</strong> Das ist noch einfacher! Wähle auf deinem Computer eine oder mehrere Dateien aus, halte die Maustaste gedrückt und ziehe sie direkt in das passende Feld. Lass die Maustaste los – fertig!</li>
              </ul>
              <p className="mt-3 text-sm text-gray-600">
                <strong>Tipp:</strong> Wenn du einen ganzen Projektordner hochladen willst, nutze am besten den Ordner-Button. So werden alle Unterordner automatisch mit übernommen.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-me-orange mb-3">So organisierst du deine Dateien</h3>
              <p>
                Nach dem Hochladen siehst du eine Liste aller Dateien. Du kannst sie in der richtigen Reihenfolge sortieren – das ist wichtig, damit die KI weiß, welche Dateien zusammengehören.
                <br/><br/>
                <strong>So sortierst du:</strong> Klicke auf eine Datei, halte die Maustaste gedrückt und ziehe sie an die gewünschte Stelle. Die anderen Dateien rücken automatisch zur Seite.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-me-orange mb-3">Die kleinen Buttons erklärt</h3>
              <p>Neben jeder Datei findest du kleine Symbole. So benutzt du sie:</p>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li><span className="text-me-blue font-bold">Das Häkchen:</span> Wähle aus, welche Dateien verarbeitet werden sollen. Du kannst auch mehrere gleichzeitig auswählen.</li>
                <li><span className="text-me-blue font-bold">Der Stift:</span> Damit änderst du den Namen der Datei, falls er nicht stimmt.</li>
                <li><span className="text-me-blue font-bold">Die zwei Blätter:</span> Erstellt eine genaue Kopie der Datei.</li>
                <li><span className="text-me-blue font-bold">Der Mülleimer:</span> Löscht die Datei aus der Liste.</li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-bold text-me-orange mb-3">Erstelle deine Ausgabedatei</h3>
              <p>
                Wenn du alle Dateien ausgewählt hast, klicke auf den großen orangenen <strong>"Starten"</strong>-Button. Das Tool verarbeitet nun deine Dateien und erstellt eine einzige, strukturierte Datei.
                <br/><br/>
                Danach kannst du diese Datei herunterladen und direkt an eine KI wie ChatGPT, Claude oder Gemini geben. Die KI kann dann dein ganzes Projekt auf einmal verstehen und dir gute Antworten geben.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-me-orange mb-3">Ordnerauswahl & Mehrfachmarkierung</h3>
              
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-me-blue mb-2">📁 Ordner markieren (auswählen) – automatisch mit Inhalt</h4>
                  <p className="text-sm mb-2">
                    Wenn du bei einem Ordner das Häkchen setzt, passiert etwas Praktisches: Alle Dateien und Unterordner (Ordner im Ordner), die zu diesem Ordner gehören, werden automatisch auch markiert (ausgewählt). Das spart dir Zeit, weil du nicht jeden einzelnen Eintrag anklicken musst.
                  </p>
                  <p className="text-sm mb-2">
                    <strong>Beispiel:</strong> Du hast einen Ordner namens „Bilder" mit vielen Fotos darin. Wenn du das Häkchen bei „Bilder" setzt, werden alle Fotos in diesem Ordner sofort auch markiert.
                  </p>
                  <p className="text-sm">
                    <strong>Wichtig:</strong> Wenn du das Häkchen bei einem Ordner wieder entfernst, werden auch alle Dateien und Unterordner darin wieder abgewählt.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-me-blue mb-2">🗑️ Mehrere Einträge markieren und löschen</h4>
                  <p className="text-sm mb-2">
                    Du kannst mehrere Dateien oder Ordner gleichzeitig markieren (ausgewählt lassen), indem du bei jedem einzelnen das Häkchen setzt. Wenn du dann bei einem dieser markierten Einträge auf das rote Papierkorb-Symbol (Icon (Symbol)) klickst, öffnet sich ein großes Fenster in der Mitte deines Bildschirms. Das nennt man ein <strong>Modal (Dialogfenster)</strong>.
                  </p>
                  <p className="text-sm mb-2">
                    In diesem Fenster siehst du eine Frage in großen, fetten Buchstaben:
                  </p>
                  <p className="text-sm font-bold text-center my-3 text-me-blue">
                    „Meinst du nur diesen Eintrag oder alle markierten Einträge?"
                  </p>
                  <p className="text-sm mb-2">
                    Darunter gibt es zwei Buttons (Schaltflächen):
                  </p>
                  <ul className="list-disc pl-6 text-sm space-y-1 mb-2">
                    <li><strong>„Alle markierten Einträge":</strong> Wenn du darauf klickst, werden alle Dateien und Ordner gelöscht, die du vorher markiert hast.</li>
                    <li><strong>„Nur dieser Eintrag":</strong> Wenn du darauf klickst, wird nur die Datei oder der Ordner gelöscht, bei dem du auf das Papierkorb-Symbol geklickt hast. Die anderen markierten Einträge bleiben unverändert.</li>
                  </ul>
                  <p className="text-sm">
                    <strong>Merke dir deine Wahl:</strong> Wenn du einmal eine der beiden Optionen ausgewählt hast, merkt sich das Tool deine Entscheidung. Beim nächsten Mal, wenn du auf den Papierkorb klickst und mehrere Einträge markiert sind, wird automatisch das gemacht, was du beim letzten Mal gewählt hast. Du musst also nicht jedes Mal neu entscheiden. Das nennt man <strong>Standard (Voreinstellung)</strong>.
                  </p>
                  <p className="text-sm mt-2">
                    <strong>Beispiel:</strong> Du klickst auf „Alle markierten Einträge". Beim nächsten Mal, wenn du wieder mehrere Einträge markiert hast und auf den Papierkorb klickst, werden automatisch alle markierten Einträge gelöscht, ohne dass das Fenster (Modal (Dialogfenster)) nochmal erscheint.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-me-blue mb-2">👁️ „Aus Auswahl entfernen" – der große rote Button</h4>
                  <p className="text-sm mb-2">
                    Wenn du mehrere Einträge markiert hast, erscheint oben in der Liste ein großer, roter Button (Schaltfläche) mit der Aufschrift <strong>„Aus Auswahl entfernen"</strong>. Dieser Button ist extra auffällig gestaltet (groß, farbig, mit einer Animation (Bewegungseffekt)), damit du ihn sofort siehst.
                  </p>
                  <p className="text-sm mb-2">
                    <strong>Was macht dieser Button?</strong> Wenn du darauf klickst, werden alle markierten Dateien und Ordner aus der Liste entfernt. Sie sind dann nicht mehr sichtbar und werden auch nicht mehr verarbeitet, wenn du auf „Starten" klickst.
                  </p>
                  <p className="text-sm">
                    <strong>Wichtig:</strong> „Aus Auswahl entfernen" bedeutet nicht „löschen". Die Dateien werden nur aus der aktuellen Ansicht (UI (Benutzeroberfläche)) entfernt. Auf deinem Computer bleiben sie unverändert.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-me-orange mb-3">Einstellungen im Detail</h3>
              <p className="mb-4">In den Einstellungen (das Zahnrad-Symbol oben rechts) kannst du das Tool an deine Bedürfnisse anpassen. Hier sind alle Optionen erklärt:</p>
              
              <div className="space-y-4">
                <div className="p-4 bg-me-bg rounded-lg">
                  <h4 className="font-bold text-me-blue mb-2">📋 Allgemein</h4>
                  <ul className="list-disc pl-6 space-y-2 text-sm">
                    <li><strong>Projekt-Sicherung:</strong> Speichert dein komplettes Projekt inklusive aller Dateien und Einstellungen als Backup-Datei. Du kannst diese Datei später wieder importieren.</li>
                    <li><strong>Einstellungen-Sicherung:</strong> Speichert nur deine persönlichen Einstellungen (ohne Dateien). Praktisch, wenn du auf einem anderen Computer arbeiten willst.</li>
                    <li><strong>Updates:</strong> Prüft, ob eine neue Version des Tools verfügbar ist und installiert sie.</li>
                  </ul>
                </div>

                <div className="p-4 bg-me-bg rounded-lg">
                  <h4 className="font-bold text-me-blue mb-2">📄 Ausgabe</h4>
                  <ul className="list-disc pl-6 space-y-2 text-sm">
                    <li><strong>ASCII-Baumstruktur:</strong> Fügt eine visuelle Übersicht deiner Projektstruktur ein (wie ein Stammbaum). Die KI versteht so besser, welche Dateien wo liegen.</li>
                    <li><strong>Symbol-Index:</strong> Erstellt ein Inhaltsverzeichnis mit allen Klassen und Funktionen in deinem Code. Die KI findet so schneller, was sie sucht.</li>
                    <li><strong>API-Endpunkt-Map:</strong> Listet alle API-Routen auf (z.B. /api/users). Nützlich für Web-Projekte.</li>
                    <li><strong>Datenbank-Schema:</strong> Zeigt automatisch alle Datenbank-Tabellen und ihre Struktur an.</li>
                    <li><strong>Smart-Squeeze:</strong> Entfernt Kommentare und unnötige Leerzeilen aus dem Code. Spart Tokens (= Kosten) bei der KI-Nutzung.</li>
                    <li><strong>Aufgaben-Beschreibung:</strong> Hier kannst du der KI sagen, was sie mit dem Code machen soll (z.B. "Finde Fehler" oder "Erkläre mir den Code").</li>
                  </ul>
                </div>

                <div className="p-4 bg-me-bg rounded-lg">
                  <h4 className="font-bold text-me-blue mb-2">🎯 KI-Optimierung</h4>
                  <ul className="list-disc pl-6 space-y-2 text-sm">
                    <li><strong>Ziel-Modell:</strong> Wähle aus, welche KI du nutzen willst (z.B. GPT-4, Claude, Gemini). Jede KI hat unterschiedliche Kosten.</li>
                    <li><strong>Formatierung:</strong> Legt fest, wie die Ausgabe aussehen soll (Markdown für Menschen, XML für Claude, JSON für Maschinen).</li>
                    <li><strong>Token-Limit:</strong> Setzt eine Obergrenze für die Dateigröße. Wenn dein Projekt zu groß ist, wird es automatisch in mehrere Teile aufgeteilt.</li>
                    <li><strong>Live-Token-Schätzung:</strong> Zeigt dir sofort an, wie viele Tokens (und damit Kosten) dein Projekt verursachen wird.</li>
                    <li><strong>Dependency-Crawler:</strong> Findet automatisch alle Dateien, die dein Code importiert, und fügt sie hinzu.</li>
                    <li><strong>Tech-Stack-Autodetect:</strong> Erkennt automatisch, welche Programmiersprachen und Frameworks du nutzt.</li>
                  </ul>
                </div>

                <div className="p-4 bg-me-bg rounded-lg">
                  <h4 className="font-bold text-me-blue mb-2">💰 Token-Preise & automatische Updates</h4>

                  <p className="text-sm text-me-blue mb-3">
                    Ein „Token" ist ein kleines Textstück. Grob gesagt: Ein paar Buchstaben oder ein kurzes Wort. Wenn du Text an ein KI‑Modell schickst, werden die Zeichen in Tokens zerlegt.
                    Die Kosten werden meistens getrennt berechnet für <strong>Input (dein Text)</strong> und <strong>Output (Antwort der KI)</strong>.
                  </p>

                  <div className="text-sm text-me-blue space-y-2">
                    <p><strong>Warum gibt es eine Token‑Berechnung?</strong></p>
                    <ul className="list-disc ml-5 space-y-1">
                      <li>Damit du siehst, wie „groß" dein Projekt ist (wie viel Text du wirklich an die KI sendest).</li>
                      <li>Damit du grob abschätzen kannst, wie teuer ein Durchlauf wird (vor allem bei API‑Nutzung).</li>
                      <li>Damit du nicht aus Versehen ein Modell überlastest (jedes Modell hat ein Kontextfenster (Context Window – maximaler Textumfang)).</li>
                    </ul>

                    <p className="mt-3"><strong>Wichtig:</strong> ChatGPT als Abo (Web/Chat‑App) ist nicht dasselbe wie API‑Nutzung.</p>
                    <ul className="list-disc ml-5 space-y-1">
                      <li><strong>ChatGPT (Abo):</strong> Du zahlst meist einen festen monatlichen Preis. Du siehst dort keine exakten Token‑Kosten pro Anfrage.</li>
                      <li><strong>API (Programmierschnittstelle):</strong> Du zahlst „pay‑as‑you‑go" (bezahle pro Nutzung). Hier sind Token‑Preise wichtig, weil jede Anfrage Geld kostet.</li>
                    </ul>

                    <p className="mt-3"><strong>Automatische Preis‑Updates in meRepomix‑Wrapper</strong></p>
                    <ul className="list-disc ml-5 space-y-1">
                      <li>Die App lädt eine vereinheitlichte Preisliste in den Browser‑Cache (LocalStorage – Speicher im Browser).</li>
                      <li>Wenn der Cache älter als <strong>24 Stunden</strong> ist, startet beim Öffnen der „KI‑Modell‑Einstellungen" automatisch ein Hintergrund‑Update.</li>
                      <li>Wenn der Cache älter als <strong>7 Tage</strong> ist, erscheint ein Warnhinweis, damit du weißt: Preise könnten veraltet sein.</li>
                      <li>Du kannst jederzeit manuell auf <strong>„Preise aktualisieren"</strong> klicken.</li>
                    </ul>

                    <p className="mt-3"><strong>Woher kommen die Preise?</strong></p>
                    <ul className="list-disc ml-5 space-y-1">
                      <li>Die App lädt eine öffentliche JSON‑Quelle (Datenliste) mit Modell‑Preisen und Token‑Limits.</li>
                      <li>Wenn ein Update nicht klappt (z. B. kein Internet oder Quelle blockiert), bleibt der letzte Cache erhalten.</li>
                    </ul>

                    <p className="mt-3"><strong>Beispiel 1: Warum kann es teuer werden?</strong></p>
                    <ul className="list-disc ml-5 space-y-1">
                      <li>Du gibst der KI sehr viel Text (viele Dateien) und verlangst eine sehr lange Antwort.</li>
                      <li>Dann steigen <strong>Input‑Tokens</strong> (weil du viel sendest) und <strong>Output‑Tokens</strong> (weil die KI viel schreibt).</li>
                      <li>Wenn ein Modell beim Output teurer ist als beim Input, kann eine sehr lange Antwort überraschend teuer sein.</li>
                    </ul>

                    <p className="mt-3"><strong>Beispiel 2: Wie wird es preiswert?</strong></p>
                    <ul className="list-disc ml-5 space-y-1">
                      <li>Du teilst große Projekte in kleinere Stücke (Chunking (Aufteilen)).</li>
                      <li>Du nutzt ein günstigeres Modell für Vorarbeiten (z. B. grobe Zusammenfassung) und ein stärkeres Modell nur für den finalen Schritt.</li>
                      <li>Du begrenzt die Antwortlänge (z. B. „max. 300 Wörter" oder „nur Stichpunkte").</li>
                    </ul>

                    <p className="mt-3"><strong>Welches Modell wofür?</strong> (Faustregeln)</p>
                    <ul className="list-disc ml-5 space-y-1">
                      <li><strong>Günstiges Modell:</strong> schnelle Aufgaben, grobe Zusammenfassung, erste Ideen, einfache Umformulierungen.</li>
                      <li><strong>Stärkeres Modell:</strong> schwierige Logik, Code‑Analyse, genaue Fehlerdiagnose, hochwertige Texte mit Struktur.</li>
                      <li><strong>Großes Kontextfenster:</strong> wenn du viele Dateien/Code am Stück brauchst (sonst muss stärker gechunked werden).</li>
                    </ul>

                    <p className="mt-3"><strong>Was bedeutet „Token‑Limit" in den Einstellungen?</strong></p>
                    <ul className="list-disc ml-5 space-y-1">
                      <li>Das ist die maximale Größe der Textstücke beim Aufteilen.</li>
                      <li>Wenn dein Token‑Limit höher ist als das Kontextfenster des Modells, kann das Modell den Text nicht komplett „sehen".</li>
                      <li>Darum zeigt dir die App das Kontextfenster (Token‑Limit) des gewählten Modells an, sofern verfügbar.</li>
                    </ul>
                  </div>
                </div>

                <div className="p-4 bg-me-bg rounded-lg">
                  <h4 className="font-bold text-me-blue mb-2">🛡️ Sicherheit</h4>
                  <ul className="list-disc pl-6 space-y-2 text-sm">
                    <li><strong>PII-Scrubber:</strong> Entfernt automatisch persönliche Daten wie E-Mail-Adressen, IP-Adressen und API-Schlüssel aus dem Code. Schützt deine Privatsphäre!</li>
                  </ul>
                </div>

                <div className="p-4 bg-me-bg rounded-lg">
                  <h4 className="font-bold text-me-blue mb-2">⚡ Erweitert</h4>
                  <ul className="list-disc pl-6 space-y-2 text-sm">
                    <li><strong>Git-Awareness:</strong> Nutzt Git, um nur die Dateien zu bündeln, die du seit dem letzten Commit geändert hast. Spart Zeit!</li>
                    <li><strong>Clipboard-Export:</strong> Kopiert die fertige Ausgabe direkt in deine Zwischenablage. Du kannst sie dann sofort in die KI einfügen.</li>
                    <li><strong>Incremental Mode:</strong> Sendet nur die Änderungen seit dem letzten Export. Spart massiv Tokens bei langen Projekten.</li>
                    <li><strong>Fokus-Preset:</strong> Filtert Dateien nach Typ (z.B. nur Frontend-Dateien oder nur Backend-Code).</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-xl font-bold text-me-orange mb-3">Nützliche Tipps</h3>
              <ul className="list-disc pl-6 mt-2 space-y-2">
                <li><strong>Token-Anzeige:</strong> Schau oben auf die Token-Schätzung. Sie zeigt dir, wie groß deine Datei für die KI ist. Je mehr Tokens, desto mehr kostet die Nutzung.</li>
                <li><strong>Backup erstellen:</strong> In den Einstellungen kannst du eine Sicherung deines Projekts erstellen. Das ist praktisch, wenn du später weitermachen willst.</li>
                <li><strong>Einstellungen anpassen:</strong> In den Einstellungen kannst du auswählen, welche Informationen automatisch eingefügt werden sollen (z.B. eine Baumstruktur oder eine Liste aller Funktionen).</li>
                <li><strong>Smart-Squeeze nutzen:</strong> Wenn dein Projekt sehr groß ist, aktiviere "Smart-Squeeze" in den Einstellungen. Das entfernt unnötige Kommentare und spart Tokens.</li>
                <li><strong>PII-Scrubber aktivieren:</strong> Wenn du deinen Code online teilen willst, aktiviere den PII-Scrubber. Er entfernt automatisch sensible Daten wie E-Mail-Adressen.</li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-bold text-me-orange mb-3">Häufige Fragen</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-me-blue mb-1">Was sind "Tokens"?</h4>
                  <p className="text-sm">Tokens sind kleine Texteinheiten, die KIs zum Lesen verwenden. Je mehr Text, desto mehr Tokens. Die meisten KIs berechnen ihre Kosten nach Tokens.</p>
                </div>
                <div>
                  <h4 className="font-semibold text-me-blue mb-1">Warum sollte ich die Ausgabe herunterladen?</h4>
                  <p className="text-sm">Die heruntergeladene Datei enthält dein komplettes Projekt in einem Format, das KIs perfekt verstehen. Du kannst sie einfach in ChatGPT, Claude oder andere KIs hochladen.</p>
                </div>
                <div>
                  <h4 className="font-semibold text-me-blue mb-1">Was passiert mit meinen Dateien?</h4>
                  <p className="text-sm">Alle Dateien bleiben auf deinem Computer. Das Tool lädt nichts ins Internet hoch. Alles wird lokal in deinem Browser verarbeitet.</p>
                </div>
              </div>
            </section>
          </div>

          <div className="mt-6 pt-4 border-t border-me-blue/10 text-center shrink-0">
            <button 
              onClick={onClose}
              className="px-8 py-3 bg-me-orange text-white rounded-lg font-bold hover:bg-me-orange/90 transition-colors shadow-md"
            >
              Verstanden!
            </button>
          </div>
        </div>
      </NeonBorder>
    </div>
  );
};

export default UserManual;